/** SUNTRONIX ONE — Universal scanner input hook.
 *
 *  Supports every scanner class a warehouse operator might carry:
 *
 *    • Camera         — via native BarcodeDetector where available
 *    • USB HID        — the hardware types the barcode as fast key
 *                        presses followed by Enter.  We detect that by
 *                        clustering keydowns arriving within 40ms of
 *                        each other and no focused text-input.
 *    • Bluetooth HID  — behaves as USB HID for the browser (same path)
 *    • Keyboard-wedge — identical detection to USB HID
 *
 *  Consumers pass a single `onCode(value)` callback.  The hook exposes
 *  the last-seen source ("camera" | "wedge") so the UI can render a
 *  little indicator.
 */
import { useEffect, useRef, useState } from "react";

const WEDGE_KEY_GAP_MS = 40;
const WEDGE_MIN_LEN    = 4;

export function useWedgeScanner(onCode, { enabled = true } = {}) {
  const buf = useRef("");
  const lastAt = useRef(0);
  const [lastSource, setLastSource] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      // Ignore when the operator is typing into an input.
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" ||
          e.target?.isContentEditable) return;

      const now = Date.now();
      if (now - lastAt.current > 250) buf.current = "";
      lastAt.current = now;

      if (e.key === "Enter") {
        const code = buf.current.trim();
        buf.current = "";
        if (code.length >= WEDGE_MIN_LEN) {
          setLastSource("wedge");
          onCode(code);
        }
        return;
      }
      // Ignore modifiers, tab, escape, arrows.
      if (e.key.length === 1) {
        // Only accept fast-typed characters — human typing has larger gaps.
        const gap = now - (lastAt.current - 1); // approximate
        if (buf.current.length === 0 || gap < WEDGE_KEY_GAP_MS * 8) {
          buf.current += e.key;
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCode, enabled]);

  return { lastSource, markCameraSource: () => setLastSource("camera") };
}


// Bluetooth HID barcode scanners advertise as keyboards to the OS, so
// they use the wedge path.  We expose a helper to prompt for Web
// Bluetooth pairing purely for the operator UX — most scanners will
// have been paired at the OS level and don't need this.
export async function pairBluetoothScanner() {
  if (typeof navigator === "undefined" || !navigator.bluetooth) {
    throw new Error("Web Bluetooth not available on this device / browser.");
  }
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: [0x1812, 0x180F], // HID over GATT, Battery
  });
  return { name: device.name, id: device.id };
}
